<template>
    <!-- eslint-disable-next-line vue/no-v-html -- markup the host sanitized on save, see the docblock -->
    <div class="rich-text-content min-w-0 break-words" @click="loadEmbedOnPlay" v-html="renderedHtml"></div>
</template>

<script>
import { autoplayingSrc, embedHostAllowed } from '../../js/utils/richTextEmbeds.js';

const DEFAULT_LABELS = { play: 'Play video from {host}', playAudio: 'Play audio from {host}' };

const PLAY_BUTTON_CLASS =
    'inline-flex min-h-9 cursor-pointer items-center gap-1.5 rounded border border-theme-border bg-transparent px-3 py-1 text-sm text-theme-text hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent';
/**
 * Holds every embed back behind a play button naming its provider, and sends
 * every link out to a new tab. The iframe keeps every attribute the host gave
 * it (sandbox, allow, referrer policy), but its src moves to data-src so no
 * player loads until the reader asks for one. A list that framed every video
 * would load one third-party player per row. Only https sources survive,
 * which is all the host stores anyway.
 * @param {string} html - Sanitized markup
 * @param {{play: string, playAudio: string}} labels - Texts with a {host} placeholder
 * @param {string[]} audioHosts - Providers whose players have no picture
 * @param {boolean} inlineEmbeds - Frame every iframe right away instead of holding it behind a button
 * @returns {string}
 */
function prepareMarkup(html, labels, audioHosts, inlineEmbeds) {
    if (!/<(iframe|a)\b/i.test(html)) {
        return html;
    }

    const parsed = new DOMParser().parseFromString(html, 'text/html');

    prepareEmbeds(parsed, labels, audioHosts, !inlineEmbeds);

    // The reader runs in an iframe, so a link left in the frame navigates the
    // reader away from the book.
    parsed.querySelectorAll('a[href]').forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener');
    });

    return parsed.body.innerHTML;
}

/**
 * @param {Document} parsed
 * @param {{play: string, playAudio: string}} labels
 * @param {string[]} audioHosts
 * @param {boolean} holdBack - Put the player behind a play button instead of framing it
 */
function prepareEmbeds(parsed, labels, audioHosts, holdBack) {
    parsed.querySelectorAll('iframe').forEach(iframe => {
        const source = (iframe.getAttribute('src') || '').trim();
        // A protocol-relative source would take the page's scheme, and the
        // reader only frames https.
        const protocolRelative = source.startsWith('//');
        let url;
        try {
            url = new URL(protocolRelative ? `https:${source}` : source);
        } catch {
            iframe.remove();
            return;
        }

        if (url.protocol !== 'https:') {
            iframe.remove();
            return;
        }

        if (protocolRelative) iframe.setAttribute('src', url.toString());

        const isAudio = embedHostAllowed(url.toString(), audioHosts);

        const embed = parsed.createElement('div');
        embed.className = 'rich-text-embed my-2';
        // Drives the sizing below. It lives on the wrapper this function
        // creates, never on the iframe, so nothing about it reaches what the
        // host stores.
        if (isAudio) embed.setAttribute('data-kind', 'audio');
        iframe.replaceWith(embed);
        embed.append(iframe);

        if (!holdBack) return;

        // The widget host, not the page one: a SoundCloud player is served
        // from w.soundcloud.com, and the reader knows the provider by its name.
        const host = url.hostname.replace(/^(www|player|w)\./, '');

        const play = parsed.createElement('button');
        play.type = 'button';
        play.className = PLAY_BUTTON_CLASS;
        play.setAttribute('data-embed-play', '');
        play.setAttribute('aria-label', (isAudio ? labels.playAudio : labels.play).replace('{host}', host));
        play.textContent = `▶ ${host}`;

        const controls = parsed.createElement('div');
        controls.className = 'rich-text-embed__controls';
        controls.append(play);

        iframe.setAttribute('data-src', url.toString());
        iframe.removeAttribute('src');
        iframe.hidden = true;
        embed.prepend(controls);
    });
}

/**
 * Renders rich text for two callers: a note's `annotationHtml`, and an
 * add-on's body. The rich editor's output comes back sanitized by the host
 * through the editor's schema, but a legacy add-on body (`bodyFormat: null`,
 * Trix or an integration) arrives as the host stored it, untouched by that
 * schema. This component renders both the way the old add-on renderer did,
 * with v-html, so it adds no trust either caller did not already have.
 *
 * Embeds wait behind a play button and load in place when the reader clicks
 * it, unless `inlineEmbeds` frames them right away. A player that is out of
 * sight is put back behind its button with `releaseEmbeds`, which the caller
 * owning that visibility calls.
 */
export default {
    name: 'RichTextContent',
    props: {
        html: {
            type: String,
            required: true,
        },
        inlineEmbeds: {
            type: Boolean,
            default: false,
        },
        /** Text of the play button, with a {host} placeholder; English when absent. */
        labels: {
            type: Object,
            default: null,
        },
        /**
         * Providers whose embeds are players with no picture, from the host's
         * allowlist. Empty means every embed is sized as video, which is what
         * a host that predates audio embeds gets.
         */
        audioHosts: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        renderedHtml() {
            return prepareMarkup(
                this.html,
                { ...DEFAULT_LABELS, ...(this.labels ?? {}) },
                this.audioHosts,
                this.inlineEmbeds
            );
        },
    },
    methods: {
        /**
         * The markup is static HTML, so the play buttons are answered here by
         * delegation: the player loads in place, already playing, and the
         * controls leave. The change lives in the DOM only, which is fine: the
         * markup is re-rendered only when the note or the add-on itself changes.
         * @param {MouseEvent} event
         */
        loadEmbedOnPlay(event) {
            const embed = event.target.closest?.('[data-embed-play]')?.closest('.rich-text-embed');
            const iframe = embed?.querySelector('iframe[data-src]');

            if (!iframe) {
                return;
            }

            iframe.src = autoplayingSrc(iframe.getAttribute('data-src'));
            iframe.removeAttribute('data-src');
            iframe.hidden = false;
            // Hidden rather than removed: releaseEmbeds puts the same button back.
            embed.querySelector('.rich-text-embed__controls')?.toggleAttribute('hidden', true);
        },
        /**
         * Puts every playing embed back behind its button. Dropping the src is
         * what stops the sound: a third-party player cannot be paused from the
         * outside, and the provider-specific message APIs would cover only the
         * providers we happen to know. The source it holds is the one the host
         * stored, without the autoplay the click added, so the next click starts
         * it from the top rather than resuming a player nobody can see. A player
         * framed right away has no button to go back behind, so it is swapped
         * for a fresh frame that waits unplayed.
         */
        releaseEmbeds() {
            if (this.inlineEmbeds) {
                this.$el
                    .querySelectorAll('.rich-text-embed iframe')
                    .forEach(iframe => iframe.replaceWith(iframe.cloneNode()));
                return;
            }

            this.$el.querySelectorAll('.rich-text-embed iframe[src]').forEach(iframe => {
                const source = new URL(iframe.src);
                source.searchParams.delete('autoplay');

                iframe.setAttribute('data-src', source.toString());
                iframe.removeAttribute('src');
                iframe.hidden = true;
                iframe
                    .closest('.rich-text-embed')
                    ?.querySelector('.rich-text-embed__controls')
                    ?.toggleAttribute('hidden', false);
            });
        },
    },
};
</script>

<style scoped>
/* Tailwind's preflight strips the list bullets and the link underline this
   markup relies on; these are the rules the editor applies to its document. */
.rich-text-content :deep(ul) {
    list-style: disc;
    padding-left: 1.25rem;
}

.rich-text-content :deep(ol) {
    list-style: decimal;
    padding-left: 1.25rem;
}

.rich-text-content :deep(a) {
    text-decoration: underline;
}

/* An empty paragraph is a blank line the reader typed; keep it as tall as one. */
.rich-text-content :deep(p:empty) {
    min-height: 1.45em;
}

.rich-text-content :deep(img) {
    max-width: 100%;
}

.rich-text-content :deep(iframe) {
    width: 100%;
    max-width: 100%;
    aspect-ratio: 16 / 9;
    border: 0;
}

/* An audio player draws a fixed-height strip and nothing else, so the video
   box leaves it stranded in empty space. SoundCloud's widget is the taller of
   the two at 166px; Spotify's compact player fits inside it. */
.rich-text-content :deep([data-kind='audio'] iframe) {
    aspect-ratio: auto;
    height: 166px;
}
</style>
